import React, { useEffect, useMemo, useRef, useState } from 'react';

import deepEqual from 'deep-equal';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { SearchPageProps } from '@atlassian/search-page/search-page-props';
import { SearchPageSideBySide } from '@atlassian/search-page/ui';

import {
	ANALYTIC_TYPE,
	ANALYTICS_CHANNEL,
	EVENT_TYPE,
	SideBySideAnalyticsProvider,
} from '../../utils/analytics';

import { SideBySideDivider } from './side-by-side-divider';
import type { Side } from './side-by-side-divider';
import type { EnterpriseFeedbackComparisonProps } from './types';

/**
 * Extracts shared query params and side-specific overrides from the URL query params.
 * - leftOverrides and rightOverrides from URL are transformed into 'overrides' for each SearchPage
 * - All other allowed params are shared between both SearchPages
 */
const filterQueryParams = (
	queryParams: SearchPageProps['shell']['queryParams'],
	side: 'left' | 'right',
) => {
	if (!queryParams) {
		return {};
	}

	const filteredQueryParams: NonNullable<SearchPageProps['shell']['queryParams']> = {
		...queryParams,
	};

	// Transform leftOverrides or rightOverrides into 'overrides' based on side
	const experimentsKey = side === 'left' ? 'leftOverrides' : 'rightOverrides';
	if (queryParams[experimentsKey]) {
		filteredQueryParams.overrides = queryParams[experimentsKey];
	}

	return filteredQueryParams;
};

const SearchPageWithAbstractedQueryParams = (
	props: SearchPageProps & { side: 'left' | 'right' },
) => {
	const [queryParams, setQueryParams] = useState(
		filterQueryParams(props.shell.queryParams, props.side),
	);
	const previousQueryParams = useRef(queryParams);

	useEffect(() => {
		const filteredQueryParams = filterQueryParams(props.shell.queryParams, props.side);

		if (deepEqual(filteredQueryParams, previousQueryParams.current)) {
			return;
		}

		setQueryParams(filteredQueryParams);

		previousQueryParams.current = filteredQueryParams;
	}, [props.shell.queryParams, props.side]);

	const shellWithAbstractedQueryParams = useMemo(() => {
		return {
			...props.shell,
			queryParams,
		};
	}, [props.shell, queryParams]);

	const callbacksWithAbstractedQueryParams = useMemo(() => {
		return {
			...props.callbacks,
			updateQueryParameters: (queryParams: SearchPageProps['shell']['queryParams']) => {
				setQueryParams(queryParams ?? {});
			},
		};
	}, [props.callbacks]);

	return (
		<SearchPageSideBySide
			{...props}
			shell={shellWithAbstractedQueryParams}
			callbacks={callbacksWithAbstractedQueryParams}
		/>
	);
};

const styles = cssMap({
	container: {
		display: 'flex',
		justifyContent: 'center',
	},
	searchItem: {
		flex: 1,
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
	},
	searchItemHidden: {
		display: 'none',
	},
});

function EnterpriseFeedbackComparisonPageInner(
	props: EnterpriseFeedbackComparisonProps,
): JSX.Element {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			name: 'sideBySideComparisonScreen',
			type: ANALYTIC_TYPE.SEND_SCREEN_EVENT,
			eventType: EVENT_TYPE.SCREEN,
			source: ANALYTICS_CHANNEL,
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent]);

	const [expandedSide, setExpandedSide] = useState<Side>(null);

	return (
		<Box xcss={styles.container}>
			<Box xcss={expandedSide === 'right' ? styles.searchItemHidden : styles.searchItem}>
				<SearchPageWithAbstractedQueryParams {...props} side="left" />
			</Box>
			<SideBySideDivider expandedSide={expandedSide} onExpandSide={setExpandedSide} />
			<Box xcss={expandedSide === 'left' ? styles.searchItemHidden : styles.searchItem}>
				<SearchPageWithAbstractedQueryParams {...props} side="right" />
			</Box>
		</Box>
	);
}

export function EnterpriseFeedbackComparisonPage(
	props: EnterpriseFeedbackComparisonProps,
): JSX.Element {
	const { shell } = props;

	return (
		<SideBySideAnalyticsProvider
			userId={shell.user?.id}
			tenantId={shell.cloudId}
			entryPoint={props.entryPoint}
		>
			<EnterpriseFeedbackComparisonPageInner {...props} />
		</SideBySideAnalyticsProvider>
	);
}
